import * as React from 'react';
import { Search } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ResourcePickerTab } from './types';
import { TAB_LABELS } from './types';

export type ResourcePickerSearchProps = {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeTab: ResourcePickerTab;
  onTabChange: (tab: ResourcePickerTab) => void;
  activeTabs: ResourcePickerTab[];
  placeholder?: string;
  /** Ref forwarded to the search input element for auto-focus. */
  inputRef?: React.Ref<HTMLInputElement>;
  showInput?: boolean;
  /** Id of the results listbox, for the combobox relationship. */
  listboxId?: string;
  /** Id of the row the cursor is on, announced without moving focus. */
  activeDescendantId?: string;
};

export const ResourcePickerSearch: React.FC<ResourcePickerSearchProps> = ({
  searchQuery,
  onSearchChange,
  activeTab,
  onTabChange,
  activeTabs,
  placeholder = 'Search tools, or describe a task…',
  inputRef,
  showInput = true,
  listboxId,
  activeDescendantId,
}) => {
  return (
    <div className="flex flex-col border-b border-border pt-4">
      {showInput && (
        <div className="mb-3 px-3">
          {/* The ring lives on the wrapper, not the input, so the icon sits
              inside the focused box rather than beside it. */}
          <div className="flex h-10 items-center gap-2 rounded-lg border border-border bg-background px-3 shadow-sm transition-colors focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-ring">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              // Focus stays here while the cursor moves through the list, so
              // the field has to point at the active row for it to be announced.
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-activedescendant={activeDescendantId}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              // Prevent the TipTap editor from stealing focus back.
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      {activeTabs.length > 1 && (
        // The picker is a fixed-width popover, so the row runs out of space
        // once an org has enough resource types to fill it. Scroll rather than
        // wrap or squash: a second row would push the results list down every
        // time, and shrunken tabs truncate their labels.
        <div className="no-scrollbar mb-3 flex gap-1 overflow-x-auto px-3">
          {activeTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => onTabChange(tab)}
              className={cn(
                'shrink-0 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                activeTab === tab
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
